import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { CustomerProfileDto } from '@restaurant-mis/event-api-types';

export type SocialProvider = 'google' | 'facebook' | 'instagram';

export interface CustomerSocialLoginResponse {
  token: string;
  customer: CustomerProfileDto;
  trackingJwt?: string;
}

export interface AuthProviders {
  google: boolean;
  facebook: boolean;
  instagram: boolean;
  instagramAppId?: string;
}

@Injectable({ providedIn: 'root' })
export class CustomerAuthService {
  private readonly tokenKey = 'customer_jwt_token';

  readonly currentCustomer = signal<CustomerProfileDto | null>(null);
  readonly isAuthenticated = signal(false);

  constructor(private readonly http: HttpClient) {
    this.restoreSession();
  }

  getProviders(): Observable<AuthProviders> {
    return this.http
      .get<{ data: AuthProviders }>(`${environment.apiBaseUrl}/auth/providers`)
      .pipe(map((res) => res.data));
  }

  socialLogin(
    provider: SocialProvider,
    credential: string,
    opts?: { googleRefreshToken?: string; trackingJwt?: string; redirectUri?: string },
  ): Observable<CustomerSocialLoginResponse> {
    return this.http
      .post<{ data: CustomerSocialLoginResponse }>(`${environment.apiBaseUrl}/auth/social-login`, {
        provider,
        credential,
        ...(opts?.googleRefreshToken ? { googleRefreshToken: opts.googleRefreshToken } : {}),
        ...(opts?.trackingJwt ? { trackingJwt: opts.trackingJwt } : {}),
        ...(opts?.redirectUri ? { redirectUri: opts.redirectUri } : {}),
      })
      .pipe(
        tap((response) => {
          this.setToken(response.data.token);
          this.currentCustomer.set(response.data.customer);
          this.isAuthenticated.set(true);
          this.refreshProfile();
        }),
        catchError((error) => {
          console.error('Customer social login failed:', error);
          throw error;
        }),
        map((response) => response.data),
      );
  }

  logout(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.tokenKey);
    }
    this.currentCustomer.set(null);
    this.isAuthenticated.set(false);
  }

  getToken(): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(this.tokenKey);
  }

  private setToken(token: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(this.tokenKey, token);
  }

  private restoreSession(): void {
    const token = this.getToken();
    if (!token) {
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as {
        customerId?: string;
        provider?: SocialProvider;
        email?: string;
        name?: string;
        exp?: number;
      };
      if (payload.exp && payload.exp * 1000 <= Date.now()) {
        this.logout();
        return;
      }
      if (!payload.customerId || !payload.provider) {
        this.logout();
        return;
      }
      this.isAuthenticated.set(true);
      this.currentCustomer.set({
        _id: payload.customerId,
        provider: payload.provider,
        providerUserId: '',
        ...(payload.email ? { email: payload.email } : {}),
        ...(payload.name ? { name: payload.name } : {}),
      });
      this.refreshProfile();
    } catch {
      this.logout();
    }
  }

  refreshProfile(): void {
    const token = this.getToken();
    if (!token) {
      return;
    }
    this.http
      .get<{ data: CustomerProfileDto }>(`${environment.apiBaseUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .subscribe({
        next: (response) => {
          this.currentCustomer.set(response.data);
          this.isAuthenticated.set(true);
        },
        error: () => this.logout(),
      });
  }
}
